/**
 * Singleton state manager for NeighborhoodModule commute persistence
 *
 * This solves the architecture issue where NeighborhoodModule and NeighborhoodTrigger
 * are rendered separately by the module system. Each component gets its own provider instance,
 * but they share state through this singleton manager.
 */

import type { NeighborhoodActions, NeighborhoodState } from '../types';
import type { CommuteDestination, CommuteTime, TravelMode } from '../utils/commuteTypes';

type StateChangeListener = (state: NeighborhoodState) => void;

class NeighborhoodStateManager {
  private state: NeighborhoodState;
  private listeners: Set<StateChangeListener> = new Set();

  constructor() {
    this.state = {
      commute: {
        destination: null,
        times: [],
        selectedMode: undefined,
        inputValue: '',
        status: 'idle',
        error: null,
        lastCalculated: null,
      },
      viewMode: 'map',
    };
  }

  // Subscribe to state changes
  subscribe(listener: StateChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Get current state
  getState(): NeighborhoodState {
    return this.state;
  }

  // Update state and notify listeners
  private setState(updater: (prevState: NeighborhoodState) => NeighborhoodState): void {
    this.state = updater(this.state);
    this.listeners.forEach((listener) => listener(this.state));
  }

  // Action creators that update the singleton state
  getActions(): NeighborhoodActions {
    return {
      setCommuteDestination: (destination: CommuteDestination) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            destination,
            inputValue: destination.name,
            status: 'idle',
            error: null,
          },
        }));
      },

      setCommuteTimes: (times: Array<CommuteTime>) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            times,
            status: 'success',
            error: null,
            lastCalculated: new Date(),
          },
        }));
      },

      setSelectedMode: (mode: TravelMode | undefined) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            selectedMode: mode,
          },
        }));
      },

      setInputValue: (value: string) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            inputValue: value,
          },
        }));
      },

      setCommuteStatus: (status: NeighborhoodState['commute']['status']) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            status,
          },
        }));
      },

      setCommuteError: (error: string) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            status: 'error',
            error,
          },
        }));
      },

      clearCommute: () => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            destination: null,
            times: [],
            selectedMode: undefined,
            inputValue: '',
            status: 'idle',
            error: null,
            lastCalculated: null,
          },
        }));
      },

      setViewMode: (mode) => {
        this.setState((prev) => ({
          ...prev,
          viewMode: mode,
        }));
      },
    };
  }
}

// Singleton instance - shared across all provider instances
const neighborhoodStateManager = new NeighborhoodStateManager();

export default neighborhoodStateManager;
